import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  Stack,
  Avatar,
  Tab,
  Tabs,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
} from '@mui/material';
import {
  Token as TokenIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  MonetizationOn,
  EmojiEvents,
  AccountBalance,
} from '@mui/icons-material';

interface UserToken {
  user_id: string;
  firstName: string;
  lastName: string;
  email: string;
  balance: number;
  total_earned: number;
  total_spent: number;
  last_daily_login: string;
}

interface TokenTransaction {
  id: string;
  user_id: string;
  firstName: string;
  lastName: string;
  transaction_type: string;
  amount: number;
  balance_before: number;
  balance_after: number;
  description: string;
  created_at: string;
}

interface TokenStats {
  total_users: number;
  total_balance: number;
  total_earned: number;
  total_spent: number;
  avg_balance: number;
}

const TokenManagement: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [users, setUsers] = useState<UserToken[]>([]);
  const [transactions, setTransactions] = useState<TokenTransaction[]>([]);
  const [stats, setStats] = useState<TokenStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [adjustModalOpen, setAdjustModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserToken | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [adjustAmount, setAdjustAmount] = useState<number>(0);
  const [adjustReason, setAdjustReason] = useState('');
  const [adjustType, setAdjustType] = useState<'add' | 'remove'>('add');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, transactionsRes] = await Promise.all([
        api.get('/admin/tokens/stats'),
        api.get('/admin/tokens/users'),
        api.get('/admin/tokens/transactions?limit=100')
      ]);

      console.log('Stats data received:', statsRes);
      if (statsRes.data.overall) {
        setStats({
          total_users: statsRes.data.overall.totalUsers,
          total_balance: statsRes.data.overall.totalBalance,
          total_earned: statsRes.data.overall.totalEarned,
          total_spent: statsRes.data.overall.totalSpent,
          avg_balance: statsRes.data.overall.avgBalance
        });
      }

      console.log('Users data received:', usersRes);
      // Handle paginated response
      const usersList = usersRes.data.users || [];
      const formattedUsers = usersList.map((user: any) => ({
        user_id: user.user_id,
        firstName: user.firstName || 'Unknown',
        lastName: user.lastName || 'User',
        email: user.email,
        balance: user.token_balance || 0,
        total_earned: user.lifetime_earned || 0,
        total_spent: user.lifetime_spent || 0,
        last_daily_login: user.updated_at
      }));
      setUsers(formattedUsers);

      console.log('Transactions data received:', transactionsRes);
      // Handle paginated response
      const transactionsList = transactionsRes.data.transactions || [];
      const formattedTransactions = transactionsList.map((trans: any) => ({
        id: trans.id,
        user_id: trans.user_id,
        firstName: trans.firstName || 'Unknown',
        lastName: trans.lastName || 'User',
        transaction_type: trans.transaction_type,
        amount: trans.amount,
        balance_before: trans.balance_before,
        balance_after: trans.balance_after,
        description: trans.description,
        created_at: trans.created_at
      }));
      setTransactions(formattedTransactions);
    } catch (error) {
      console.error('Error fetching token data:', error);
      // Set some mock data if APIs fail
      setStats({
        total_users: 9,
        total_balance: 450,
        total_earned: 450,
        total_spent: 0,
        avg_balance: 50
      });
      setUsers([]);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAdjustTokens = async () => {
    if (!selectedUser || !adjustAmount || !adjustReason) return;

    try {
      const endpoint = adjustType === 'add' ? '/admin/tokens/add' : '/admin/tokens/remove';
      await api.post(endpoint, {
        user_id: selectedUser.user_id,
        amount: adjustAmount,
        description: adjustReason
      });

      await fetchData();
      setAdjustModalOpen(false);
      resetAdjustForm();
    } catch (error: any) {
      console.error('Error adjusting tokens:', error);
      alert(error.response?.data?.error || 'Failed to adjust tokens');
    }
  };

  const resetAdjustForm = () => {
    setAdjustAmount(0);
    setAdjustReason('');
    setAdjustType('add');
    setSelectedUser(null);
  };

  const getTransactionIcon = (type: string) => {
    if (type.includes('earned')) return <TrendingUpIcon color="success" />;
    if (type.includes('spent')) return <TrendingDownIcon color="error" />;
    if (type === 'admin_add') return <AddIcon color="primary" />;
    if (type === 'admin_remove') return <RemoveIcon color="error" />;
    return <TokenIcon />;
  };

  const getTransactionColor = (type: string) => {
    if (type.includes('earned') || type === 'admin_add') return 'success';
    if (type.includes('spent') || type === 'admin_remove') return 'error';
    return 'default';
  };

  const formatTransactionType = (type: string) => {
    return type.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const filteredUsers = users.filter(user => 
    user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Token Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage user tokens, view transactions, and control the token economy
          </Typography>
        </Box>
        <IconButton onClick={fetchData} title="Refresh">
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography color="text.secondary" variant="caption">
                      Total Users
                    </Typography>
                    <Typography variant="h5">
                      {stats.total_users}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: '#1976d2' }}>
                    <PersonIcon />
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography color="text.secondary" variant="caption">
                      Total Balance
                    </Typography>
                    <Typography variant="h5">
                      {stats.total_balance?.toLocaleString()}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: '#FFD700' }}>
                    <AccountBalance />
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography color="text.secondary" variant="caption">
                      Total Earned
                    </Typography>
                    <Typography variant="h5" color="success.main">
                      {stats.total_earned?.toLocaleString()}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: '#4CAF50' }}>
                    <TrendingUpIcon />
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography color="text.secondary" variant="caption">
                      Total Spent
                    </Typography>
                    <Typography variant="h5" color="error.main">
                      {stats.total_spent?.toLocaleString()}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: '#F44336' }}>
                    <TrendingDownIcon />
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab label="User Balances" icon={<MonetizationOn />} />
          <Tab label="Transaction History" icon={<HistoryIcon />} />
          <Tab label="Token Settings" icon={<TokenIcon />} />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {tabValue === 0 && (
        <Paper>
          <Box sx={{ p: 2 }}>
            <TextField
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              sx={{ mb: 2 }}
            />
          </Box>

          {loading ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <LinearProgress sx={{ mb: 2 }} />
              <Typography>Loading user token data...</Typography>
            </Box>
          ) : filteredUsers.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {searchTerm ? 'No users found matching your search' : 'No users with tokens found'}
              </Typography>
            </Box>
          ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell align="right">Balance</TableCell>
                  <TableCell align="right">Total Earned</TableCell>
                  <TableCell align="right">Total Spent</TableCell>
                  <TableCell align="center">Last Daily Login</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.user_id}>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar sx={{ bgcolor: '#FFD700', width: 32, height: 32 }}>
                          {user.firstName?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2">
                            {user.firstName} {user.lastName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.email}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={user.balance?.toLocaleString()} 
                        icon={<TokenIcon />}
                        color="primary"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography color="success.main">
                        +{user.total_earned?.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography color="error.main">
                        -{user.total_spent?.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {user.last_daily_login 
                        ? new Date(user.last_daily_login).toLocaleDateString()
                        : 'Never'
                      }
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setSelectedUser(user);
                          setAdjustModalOpen(true);
                        }}
                        startIcon={<TokenIcon />}
                      >
                        Adjust
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          )}
        </Paper>
      )}

      {tabValue === 1 && (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">Balance After</TableCell>
                  <TableCell>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((transaction) => (
                  <TableRow key={transaction.id}>
                    <TableCell>
                      {new Date(transaction.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {transaction.firstName} {transaction.lastName}
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getTransactionIcon(transaction.transaction_type)}
                        label={formatTransactionType(transaction.transaction_type)}
                        size="small"
                        color={getTransactionColor(transaction.transaction_type) as any}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        color={transaction.amount > 0 ? 'success.main' : 'error.main'}
                        fontWeight="bold"
                      >
                        {transaction.amount > 0 ? '+' : ''}{transaction.amount}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {transaction.balance_after}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {transaction.description}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {tabValue === 2 && (
        <Paper sx={{ p: 3 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            Token economy settings control how users earn and spend tokens throughout the platform
          </Alert>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Daily Login Rewards
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      label="Base Daily Tokens"
                      type="number"
                      defaultValue={1}
                      fullWidth
                    />
                    <TextField
                      label="7-Day Streak Bonus"
                      type="number"
                      defaultValue={5}
                      fullWidth
                    />
                    <TextField
                      label="30-Day Streak Bonus"
                      type="number"
                      defaultValue={20}
                      fullWidth
                    />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    New User Bonuses
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      label="Welcome Bonus Tokens"
                      type="number"
                      defaultValue={10}
                      fullWidth
                    />
                    <TextField
                      label="Referral Bonus"
                      type="number"
                      defaultValue={5}
                      fullWidth
                    />
                    <TextField
                      label="First Game Bonus"
                      type="number"
                      defaultValue={3}
                      fullWidth
                    />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" size="large">
              Save Settings
            </Button>
          </Box>
        </Paper>
      )}

      {/* Adjust Tokens Modal */}
      <Dialog open={adjustModalOpen} onClose={() => setAdjustModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Adjust User Tokens
        </DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Stack spacing={3} sx={{ mt: 2 }}>
              <Alert severity="info">
                Adjusting tokens for: {selectedUser.firstName} {selectedUser.lastName}
                <br />
                Current Balance: {selectedUser.balance} tokens
              </Alert>

              <FormControl fullWidth>
                <InputLabel>Action</InputLabel>
                <Select
                  value={adjustType}
                  onChange={(e) => setAdjustType(e.target.value as 'add' | 'remove')}
                  label="Action"
                >
                  <MenuItem value="add">Add Tokens</MenuItem>
                  <MenuItem value="remove">Remove Tokens</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Amount"
                type="number"
                value={adjustAmount}
                onChange={(e) => setAdjustAmount(parseInt(e.target.value) || 0)}
                fullWidth
                inputProps={{ min: 0 }}
              />

              <TextField
                label="Reason"
                value={adjustReason}
                onChange={(e) => setAdjustReason(e.target.value)}
                fullWidth
                multiline
                rows={2}
                required
              />

              {adjustAmount > 0 && (
                <Alert severity={adjustType === 'add' ? 'success' : 'warning'}>
                  New balance will be: {
                    adjustType === 'add' 
                      ? selectedUser.balance + adjustAmount
                      : Math.max(0, selectedUser.balance - adjustAmount)
                  } tokens
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAdjustModalOpen(false);
            resetAdjustForm();
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAdjustTokens}
            disabled={!adjustAmount || !adjustReason}
            color={adjustType === 'add' ? 'primary' : 'error'}
          >
            {adjustType === 'add' ? 'Add' : 'Remove'} Tokens
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TokenManagement;